import React from 'react';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Field from 'components/Field';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import CurrencyInput from 'containers/CurrencyInput';
import PlacesSearch from 'containers/PlacesSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { City } from 'models/city';
import { SpecificRent as SpecificRentModel } from 'models/specificRent';

import { Scaffold, Container, About, Form, Fields, ButtonArea, ConfirmButton } from './styles';

interface RentForm {
  pickup?: VehiclePlace;
  dropoff?: VehiclePlace;
  specificRentAnticipation?: number | '';
  maxPrice?: number | '';
}

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (data: SpecificRentModel) => void;
}

const SpecificRent = ({ show, onConfirm, onClose }: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const initialValues: RentForm = {
    pickup: undefined,
    dropoff: undefined,
    specificRentAnticipation: undefined,
    maxPrice: 0,
  };

  const validationSchema = yup.object().shape({
    pickup: yup.object().required(),
    dropoff: yup.object().required(),
    specificRentAnticipation: yup.number().min(0),
    maxPrice: yup.number().min(0),
  });

  const handlePointSetValue = (city: City) => (city ? city.description : '');

  const handleSubmit = (data: RentForm) => {
    onConfirm({
      pickup: {
        code: `${data.pickup?.code}`,
        name: `${data.pickup?.description}, ${data.pickup?.country}`,
        latitude: data.pickup?.latitude ? data.pickup?.latitude : 0,
        longitude: data.pickup?.longitude ? data.pickup?.longitude : 0,
      },
      dropoff: {
        code: `${data.dropoff?.code}`,
        name: `${data.dropoff?.description}, ${data.dropoff?.country}`,
        latitude: data.dropoff?.latitude ? data.dropoff?.latitude : 0,
        longitude: data.dropoff?.longitude ? data.dropoff?.longitude : 0,
      },
      maxPrice: data.maxPrice ? data.maxPrice : '',
      dateVehicle: data.specificRentAnticipation ? data.specificRentAnticipation : '',
    });
    onClose();
    toast.add({
      type: 'success',
      message: t('rule.specificRent.addSuccess'),
    });
  };

  return (
    <>
      <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
        <Scaffold>
          <Mobile>
            <Header goBack={onClose}>{t('rule.specificRent.header')}</Header>
          </Mobile>

          <Desktop>
            <ModalHeader title={t('rule.specificRent.header')} onClose={onClose} />
          </Desktop>

          <Container>
            <About>
              <About.Title>{t('rule.specificRent.aboutTitle')}</About.Title>
              <About.Text>{t('rule.specificRent.aboutText')}</About.Text>
            </About>
            <Formik
              initialValues={initialValues}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isValid }) => (
                <Form>
                  <Fields>
                    <Field name="pickup" label={t('rule.specificRent.pickUp')}>
                      <CustomSelect
                        noIcon
                        placeHolder={t('rule.specificRent.pickUpPlaceHolder')}
                        onSetValue={handlePointSetValue}
                      >
                        {(selectProps: CustomSelectRenderProps) => (
                          <PlacesSearch
                            {...selectProps}
                            placeHolder={t('rule.specificRent.pickUpPlaceHolder')}
                            isPickUp={true}
                          />
                        )}
                      </CustomSelect>
                    </Field>
                    <Field name="dropoff" label={t('rule.specificRent.dropOff')}>
                      <CustomSelect
                        noIcon
                        placeHolder={t('rule.specificRent.dropOffPlaceHolder')}
                        onSetValue={handlePointSetValue}
                      >
                        {(selectProps: CustomSelectRenderProps) => (
                          <PlacesSearch
                            {...selectProps}
                            placeHolder={t('rule.specificRent.pickUpPlaceHolder')}
                            isPickUp={false}
                          />
                        )}
                      </CustomSelect>
                    </Field>
                    <Field name="specificRentAnticipation" label={t('rule.restriction.anticipation')}>
                      <Input
                        type="number"
                        darkPlaceholder
                        suffix={t('rule.restriction.anticipationSuffix')}
                        placeholder={t('rule.restriction.withoutRestriction')}
                      />
                    </Field>
                    <Field name="maxPrice" label={t('rule.specificRent.maxPrice')}>
                      <CurrencyInput placeHolder={t('rule.restriction.withoutRestriction')} />
                    </Field>
                  </Fields>
                  <ButtonArea>
                    <ConfirmButton disabled={!isValid} value={t('rule.specificRent.confirm')} />
                  </ButtonArea>
                </Form>
              )}
            </Formik>
          </Container>
        </Scaffold>
      </ApplicationOverlayContent>
    </>
  );
};

export default SpecificRent;
